// src/features/billing/components/billing-summary-chip.tsx
import { AlertTriangle, CreditCard } from 'lucide-react'
import { Link } from 'react-router-dom'

import { cn } from '@/shared/utils/cn'

import type { BillingSummary } from '../billing-summary'
import { formatRenewalDate } from '../billing-summary'

const toneClass: Record<BillingSummary['tone'], string> = {
  ok: 'text-[var(--color-text-soft)] hover:bg-[var(--panel-subtle)]',
  warning:
    'text-[var(--color-warning-solid)] bg-[var(--color-warning-solid)]/10 hover:bg-[var(--color-warning-solid)]/15',
  danger:
    'text-[var(--color-danger-solid)] bg-[var(--color-danger-solid)]/10 hover:bg-[var(--color-danger-solid)]/15',
}

interface BillingSummaryChipProps {
  summary: BillingSummary & { isLoading: boolean }
}

export function BillingSummaryChip({ summary }: BillingSummaryChipProps) {
  if (summary.isLoading) {
    return (
      <span
        aria-hidden
        className="hidden h-7 w-24 animate-pulse rounded-lg bg-[var(--panel-subtle)] sm:block"
      />
    )
  }
  if (!summary.hasBalance) {
    return null
  }

  const alert = summary.tone !== 'ok'
  const overdue = summary.status === 'past_due' || summary.status === 'expired'
  const alertLabel = overdue ? 'Проблема с оплатой' : 'Низкий баланс'

  return (
    <Link
      className={cn(
        'hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-bg)] sm:flex',
        toneClass[summary.tone],
      )}
      title="Счёт: баланс токенов и дата продления"
      to="/billing"
    >
      {alert ? (
        <AlertTriangle aria-label={alertLabel} className="h-4 w-4 flex-none" role="img" />
      ) : (
        <CreditCard className="h-4 w-4 flex-none" />
      )}
      <span>{summary.tokenBalance.toLocaleString('ru-RU')}</span>
      {summary.renewalDate ? (
        <span className="hidden text-[var(--color-text-muted)] lg:inline">
          · {summary.ended ? 'истекла' : 'до'} {formatRenewalDate(summary.renewalDate)}
        </span>
      ) : null}
    </Link>
  )
}
